<template>
  <div class="container mx-auto px-4 py-8">
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Audit Logs</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">All write operations performed on this tenant</p>
      </div>
    </div>

    <!-- Filters -->
    <div class="flex flex-wrap gap-3 mb-5">
      <select v-model="filters.action" @change="fetchLogs()" class="input w-auto text-sm">
        <option value="">All actions</option>
        <option value="create">Created</option>
        <option value="update">Updated</option>
        <option value="delete">Deleted</option>
      </select>
      <select v-model="filters.status" @change="fetchLogs()" class="input w-auto text-sm">
        <option value="">All statuses</option>
        <option value="success">Success</option>
        <option value="failure">Failure</option>
      </select>
      <input v-model="filters.resource" @change="fetchLogs()" type="text" placeholder="Filter by resource…"
        class="input w-44 text-sm" />
      <input v-model="filters.start_date" @change="fetchLogs()" type="date"
        class="input w-auto text-sm" />
      <input v-model="filters.end_date" @change="fetchLogs()" type="date"
        class="input w-auto text-sm" />
      <button @click="clearFilters"
        class="px-3 py-1.5 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
        Clear
      </button>
      <button @click="exportCSV" :disabled="exporting"
        class="ml-auto px-3 py-1.5 text-xs rounded-md border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 transition-colors">
        {{ exporting ? 'Exporting…' : 'Export CSV' }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-16">
      <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 dark:border-gray-700 border-t-blue-500"></div>
    </div>

    <div v-else-if="error" class="text-center py-12 text-red-500 text-sm">{{ error }}</div>

    <div v-else-if="logs.length === 0" class="text-center py-16 text-gray-400 dark:text-gray-500 text-sm">
      No audit log entries found.
    </div>

    <div v-else class="space-y-2">
      <div v-for="log in logs" :key="log.id"
        class="card px-4 py-3 flex flex-col sm:flex-row sm:items-start gap-3"
        :class="log.status === 'failure' ? 'border-red-200 dark:border-red-800/50' : ''">

        <!-- Left: timestamp + actor -->
        <div class="flex-shrink-0 w-44 text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
          <div class="font-medium text-gray-700 dark:text-gray-300">{{ formatTime(log.performed_at) }}</div>
          <div class="truncate">{{ actorLabel(log) }}</div>
          <div class="text-gray-400 dark:text-gray-500">{{ log.ip_address }}</div>
        </div>

        <!-- Middle: human-readable summary -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold"
              :class="actionClass(log.action)">{{ actionLabel(log.action) }}</span>
            <span class="text-sm font-medium text-gray-900 dark:text-white">
              {{ humanSummary(log) }}
            </span>
            <span v-if="log.status === 'failure'"
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
              Failed
            </span>
          </div>
          <div v-if="log.error" class="mt-1 text-xs text-red-600 dark:text-red-400">{{ log.error }}</div>
          <!-- Expandable details -->
          <div v-if="log.details" class="mt-1">
            <button @click="toggleDetails(log.id)"
              class="text-xs text-blue-500 dark:text-blue-400 hover:underline">
              {{ expanded.has(log.id) ? 'Hide details' : 'Show details' }}
            </button>
            <pre v-if="expanded.has(log.id)"
              class="mt-1 text-xs bg-gray-50 dark:bg-gray-900 rounded p-2 overflow-x-auto text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{{ prettyDetails(log.details) }}</pre>
          </div>
        </div>

        <!-- Right: resource badge -->
        <div class="flex-shrink-0 text-right">
          <span class="inline-block text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded font-mono">
            {{ resourceLabel(log.resource) }}
            <template v-if="log.resource_id"> #{{ log.resource_id }}</template>
          </span>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.totalPages > 1" class="flex items-center justify-between mt-6 text-sm">
      <span class="text-gray-500 dark:text-gray-400">
        Page {{ pagination.currentPage }} of {{ pagination.totalPages }}
        ({{ pagination.totalItems }} entries)
      </span>
      <div class="flex gap-2">
        <button :disabled="pagination.currentPage <= 1"
          @click="goToPage(pagination.currentPage - 1)"
          class="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
          ← Prev
        </button>
        <button :disabled="pagination.currentPage >= pagination.totalPages"
          @click="goToPage(pagination.currentPage + 1)"
          class="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
          Next →
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useStore } from 'vuex'

const store = useStore()
const logs = ref([])
const loading = ref(false)
const error = ref(null)
const expanded = ref(new Set())

const filters = reactive({
  action: '',
  status: '',
  resource: '',
  start_date: '',
  end_date: '',
})

const pagination = reactive({
  currentPage: 1,
  totalPages: 1,
  entriesPerPage: 25,
  totalItems: 0,
})

async function fetchLogs(page = 1) {
  loading.value = true
  error.value = null
  try {
    const tenantId = store.state.currentTenantId
    const response = await store.dispatch('fetchAuditLogs', {
      tenantId,
      params: {
        page,
        per_page: pagination.entriesPerPage,
        order_by: 'performed_at',
        sort_direction: 'desc',
        action: filters.action || undefined,
        status: filters.status || undefined,
        resource: filters.resource || undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
      }
    })
    logs.value = response.data ?? []
    pagination.currentPage = response.currentPage ?? page
    pagination.totalPages = response.totalPages ?? 1
    pagination.totalItems = response.totalEntries ?? logs.value.length
  } catch (e) {
    error.value = e.message ?? 'Failed to load audit logs'
  } finally {
    loading.value = false
  }
}

function goToPage(page) { fetchLogs(page) }

function clearFilters() {
  Object.assign(filters, { action: '', status: '', resource: '', start_date: '', end_date: '' })
  fetchLogs()
}

function toggleDetails(id) {
  const next = new Set(expanded.value)
  next.has(id) ? next.delete(id) : next.add(id)
  expanded.value = next
}

// ── Display helpers ──────────────────────────────────────────────

function formatTime(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

function actorLabel(log) {
  if (log.user?.name) return log.user.name
  if (log.user?.email) return log.user.email
  if (log.user_id === 0 || log.user_id === '0') return 'Service Account'
  return `User #${log.user_id}`
}

// Map raw resource path segments to readable names
const resourceNames = {
  users: 'User', documents: 'Document', products: 'Product', vulns: 'Vulnerability',
  incidents: 'Incident', compliances: 'Compliance', updates: 'Update',
  sboms: 'SBOM', 'scan-reports': 'Scan Report', 'security-categories': 'Security Category',
  subscribers: 'Subscriber', 'customer-access': 'Customer Access', tenants: 'Tenant',
  'service-accounts': 'Service Account', 'audit-logs': 'Audit Log',
}
function resourceLabel(r) { return resourceNames[r] ?? r ?? '—' }

function actionLabel(a) {
  return { create: 'Created', update: 'Updated', delete: 'Deleted', login: 'Login', logout: 'Logout' }[a] ?? a
}

function actionClass(a) {
  return {
    create: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    update: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    delete: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  }[a] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
}

function humanSummary(log) {
  const res = resourceLabel(log.resource)
  const id  = log.resource_id ? ` #${log.resource_id}` : ''
  // Try to pull a name/title from the JSON details blob
  let name = ''
  if (log.details) {
    try {
      const d = JSON.parse(log.details)
      name = d.name ?? d.title ?? d.cve_id ?? d.synopsis ?? d.email ?? ''
      if (name) name = ` "${name}"`
    } catch (e) { /* ignore malformed JSON details */ }
  }
  const actor = actorLabel(log)
  return `${actor} ${actionLabel(log.action).toLowerCase()} ${res}${name}${id}`
}

function prettyDetails(raw) {
  try { return JSON.stringify(JSON.parse(raw), null, 2) } catch { return raw }
}

onMounted(() => fetchLogs())
</script>
